import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// INTERFACES
import { EIntegrationTypes } from '../../../channel/contants/channel.constant';
import {
  ISalesOrder,
  ESalesOrderSource,
  ESalesOrderStatus,
} from '../../../order/interfaces/order.interface';

// SERVICES
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { OrderApiService } from '../../../shared/services/api/order/order-api.service';

// UTILS
import { OrderUtilService } from '../../../shared/services/util/order-util.service';
import { trackById } from '../../../shared/util/track-by.util';
import { EShippingStatus } from '../../../shipping/interfaces/shipping.interface';
import { EOrderShippingErrors } from '../../interfaces/order-shipping.interface';

interface IOrderData extends ISalesOrder {
  errors ?:any;
  shippingStatus ?:string;
  shippingError ?:string;
}

@Component({
  selector: 'lum-order-ship-form',
  templateUrl: './order-ship-form.component.html',
  styleUrls: ['./order-ship-form.component.scss'],
})
export class OrderShipFormComponent implements OnInit, OnDestroy {
  loading :boolean = false;
  shippingStatus = EShippingStatus;
  orderShippingErrors = EOrderShippingErrors;
  shippableOrders :IOrderData[] = [];
  nonShippableOrders :IOrderData[] = [];
  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private dialogRef :MatDialogRef<OrderShipFormComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data :{
      title :string;
      salesOrders :ISalesOrder[];
      isSelectAllRows :boolean;
      remainingSelectableCount :number;
      ftEdiIntegrationEnabled :boolean;
    },
    private orderApiService :OrderApiService,
    private snackBar :MatSnackBar,
    private orderUtilService :OrderUtilService,
  ) {}

  ngOnInit() :void {
    this.initSalesOrders();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onConfirmShipOrders() :void | boolean {
    if (this.data.isSelectAllRows) {
      this.dialogRef.close(this.data.isSelectAllRows);

      return;
    }
    if (this.nonShippableOrders.length > 0) {
      return true;
    }
    this.loading = true;

    this.shippableOrders
      .filter((order :any) => order.shippingStatus !== ESalesOrderStatus.SHIPPED)
      .forEach((order :IOrderData) => {
        order.shippingStatus = EShippingStatus.SHIPPING;

        this.orderApiService
          .shipOrderToShipstation(order.id)
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe({
            next: (response) => {
              order.shippingStatus = EShippingStatus.SHIPPED;
              if (this.finishedShippingOrders) {
                this.snackBar.open('Pushing orders finished.', 'Close', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
              }
            },
            error: (err) => {
              order.shippingStatus = EShippingStatus.FAILED;
              if (this.finishedShippingOrders) {
                this.snackBar.open(
                  err?.error ? err?.error : 'Failed to push some orders.',
                  'Close',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
              }
            },
          });
      });
  }

  onClose() :void {
    this.dialogRef.close();
  }

  private initSalesOrders() :void {
    this.salesOrders.forEach((order :ISalesOrder) => {
      const orderShippabilityErrors =
        this.orderUtilService.checkOrderShippabilityErrors(order);
      if (orderShippabilityErrors.isNotShippable) {
        if (
          this.data.ftEdiIntegrationEnabled &&
          order?.channel?.pushOrderIntegrationMapping
        ) {
          this.shippableOrders.push(order);
        } else {
          this.nonShippableOrders.push({
            ...order,
            ...{
              errors: orderShippabilityErrors,
            },
          });
        }
      } else {
        this.shippableOrders.push(order);
      }
    });
  }

  get salesOrders() :ISalesOrder[] {
    return this.data.salesOrders ?? [];
  }

  get finishedShippingOrders() :boolean {
    return (
      this.shippableOrders.filter(
        (order :IOrderData) => order.shippingStatus === EShippingStatus.SHIPPING,
      ).length === 0
    );
  }

  get disableConfirmShipOrders() :boolean {
    const hasNonShippableOrders = this.nonShippableOrders.length > 0;
    const noShippableOrders = this.shippableOrders.length === 0;
    const allRowsNotSelected = !this.data.isSelectAllRows;
    const allRowsSelected = this.data.isSelectAllRows;

    if (this.loading) {
      return true;
    }

    if (hasNonShippableOrders) {
      if (allRowsNotSelected || (noShippableOrders && allRowsSelected)) {
        return true;
      }
    }

    return false;
  }
}
